import { useCallback, useEffect } from "react"
import { NavigationType, useBlocker, useLocation } from "react-router"
import {
  type Blocker,
  type BlockerFunction,
  type Location,
  useBeforeUnload
} from "react-router-dom"

// Closing or reloading the tab has no next location, but a BlockerFunction is
// always handed one. Asking it against a location that matches no route keeps
// every blocker's pathname comparison true, so the answer reduces to its
// unsaved-changes check — the only question that means anything at unload.
const UNLOAD_LOCATION: Location = {
  pathname: "",
  search: "",
  hash: "",
  state: null,
  key: "unload"
}

function usePrompt(when: boolean | BlockerFunction): Blocker {
  const blocker = useBlocker(when)
  const currentLocation = useLocation()

  useEffect(() => {
    if (blocker.state === "blocked" && !when) {
      blocker.reset()
    }
  }, [blocker, when])

  useBeforeUnload(
    useCallback(
      (event) => {
        const blocking =
          typeof when === "function"
            ? when({
                currentLocation,
                nextLocation: UNLOAD_LOCATION,
                historyAction: NavigationType.Pop
              })
            : when

        if (!blocking) return

        event.preventDefault()
        event.returnValue = "Changes that you made may not be saved."
      },
      [when, currentLocation]
    ),
    { capture: true }
  )

  return blocker
}

export default usePrompt
